/* eslint-disable no-param-reassign */
import _ from 'underscore';
import { v4 as uuidv4 } from 'uuid';
import type { Middleware } from '../request-with-middleware';
import type {
  ErrorLogger,
  ErrorMapper,
  InstrumentationStubOptions,
  PerformanceLogger,
  TraceLogger,
} from './instrumentation-utils';
import { InstrumentationContext } from './instrumentation-context';
import {
  beforeSendHandler, errorHandler, successHandler,
} from './instrumentation-utils';

export interface InstrumentationOptions {
  pageTrackingId?: string;
  errorMappers?: ErrorMapper[];
  stubOptions?: Partial<InstrumentationStubOptions>;
  loggerFunc?: {
    traceFunc?: TraceLogger;
    errorFunc?: ErrorLogger;
    perfFunc?: PerformanceLogger;
  };
  shouldAddHeaders?: (_context: InstrumentationContext) => boolean;
}

export function createInstrumentationMiddleware(
  instrumentationOptions: InstrumentationOptions = {},
): Middleware {
  const {
    pageTrackingId,
    errorMappers = [],
    stubOptions,
    loggerFunc: {
      traceFunc = _.noop,
      errorFunc = _.noop,
      perfFunc = _.noop,
    } = {},
    shouldAddHeaders = _.constant(true),
  } = instrumentationOptions;

  const isUserError = (error: unknown) =>
    _.any(errorMappers, errorMapper => Boolean(errorMapper.check(error)));

  return next => async request => {
    const requestId = request.headers?.['x-ms-requestid'] as string | undefined || uuidv4();
    const instrumentationContext = new InstrumentationContext({
      pageTrackingId,
      errorMappers,
      url: request.uri,
      requestId,
      httpMethod: request.spec.requestContract.verb,
      stubOptions,
    });
    beforeSendHandler(instrumentationContext, request, perfFunc, shouldAddHeaders);
    return next(request).then(response => {
      successHandler(instrumentationContext, response, errorFunc, perfFunc);
      return response;
    }).catch((error: unknown) => {
      errorHandler(instrumentationContext, error, isUserError, traceFunc, errorFunc, perfFunc);
      throw error;
    });
  };
}
